import { useState, type ReactNode } from 'react';
import { IconButton } from '../ui/controls';
import { useLongPress } from '../ui/longPress';
import { Menu, type MenuEntry } from '../ui/menu';
import { Face, PlaceholderCard, Thumb } from './parts';
import { relativeWhen, type FileRow, type ProjectInfo } from './rows';

/** A file as a picture with its name: the everyday tile. */
export function FileCard({
  file,
  project,
  when,
  big = false,
  onOpen,
  menu,
  line,
}: {
  file: FileRow;
  /** The project to name under it; null for none, or when the grid is one project's. */
  project: ProjectInfo | null;
  /** The moment to show: my last edit for Recents, the last change anywhere else. */
  when: string;
  big?: boolean;
  /** Left out, a press opens the file's menu: a file in the Trash has nothing to open. */
  onOpen?: () => void;
  menu: () => MenuEntry[];
  /** Said instead of the moment, as the Trash says how long a file has left. */
  line?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const said = line ?? relativeWhen(when);
  // A finger has no hover to show the ••• and no right-click: it shows always, and a long-press opens it too.
  const hold = useLongPress<HTMLDivElement>((element) => setAnchor(element));
  return (
    <div className="group/tile relative min-w-0" {...hold}>
      <IconButton
        icon="more"
        label={`More for ${file.name}`}
        className={`pointer-coarse:size-[44px]! glass absolute right-[8px] top-[8px] z-10 rounded-full transition-opacity focus-visible:opacity-100 group-hover/tile:opacity-100 pointer-coarse:opacity-100 ${anchor ? 'opacity-100' : 'opacity-0'}`}
        onClick={(event) => setAnchor(event.currentTarget)}
      />
      <button type="button" onClick={(event) => (onOpen ? onOpen() : setAnchor(event.currentTarget))} className="group block w-full text-left">
        <Thumb url={file.thumbnailUrl} eager={big} className="aspect-[4/3] rounded-[12px]" />
        <span className="mt-[9px] flex items-center gap-[8px] px-[2px]">
          <span className="min-w-0 flex-1">
            <span className={`block truncate font-medium ${big ? 'text-[14px]' : 'text-[13px]'}`}>{file.name}</span>
            <span className="flex min-w-0 items-center gap-[6px] text-[11px] text-label-2">
              {project ? (
                <>
                  <span className="truncate">{project.name}</span>
                  {said ? <span className="text-label-3">·</span> : null}
                </>
              ) : null}
              <span className="shrink-0">{said}</span>
            </span>
          </span>
          {/* Someone else's file carries their face; mine carries nothing, because it goes without saying. */}
          {file.ownedByMe === false && file.owner ? <Face owner={file.owner} /> : null}
        </span>
      </button>
      {anchor ? <Menu open anchor={anchor} onClose={() => setAnchor(null)} align="end" variant="screen" items={menu()} /> : null}
    </div>
  );
}

/** Files as a grid of cards, filling the row. */
export function FileGrid({ children, min = 190 }: { children: ReactNode; min?: number }) {
  return (
    <ul className="grid gap-x-[20px] gap-y-[24px]" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(min(${min}px, calc(50% - 10px)), 1fr))` }}>
      {children}
    </ul>
  );
}

/** A grid of grey stand-ins while a list loads. */
export function PlaceholderGrid({ count = 10, min = 190 }: { count?: number; min?: number }) {
  return (
    <FileGrid min={min}>
      {Array.from({ length: count }, (_, index) => (
        <li key={index}>
          <PlaceholderCard />
        </li>
      ))}
    </FileGrid>
  );
}
